import { apiClient } from './client';
import { Sentry } from '@/config/sentry';

export type ImageQuality = 'high' | 'medium' | 'low';

export interface ShareImageData {
  totalDistance: number;
  totalCalories: number;
  averagePace: string;
  durationDays: number;
  payoutAmount: number;
}

export interface ProgressShareData {
  rank: number;
  totalParticipants: number;
  workoutLogged: boolean;
  prizePool: number;
  activityType: string;
  distance: number;
  calories: number;
  duration: string; // minutes as string, e.g., "413"
  pace: string; // e.g., "5'23\""
  completionPercentage: number;
  daysCompleted: number;
  totalDays: number;
  challengeName?: string;
}

export interface ChallengeCreationShareData {
  duration: number; // in days
  dailyGoal: number; // in km
  startDate: string; // formatted date, e.g., "Jan 1, 2026"
  participantCount?: number; // optional participant count
}

// Timeout configuration per quality level (smaller = faster expected delivery)
const TIMEOUT_BY_QUALITY: Record<ImageQuality, number> = {
  high: 25000,   // 25s for 4K PNG
  medium: 15000, // 15s for 1080p JPEG
  low: 8000,     // 8s for 540p JPEG
};

// Quality downgrade order for adaptive retry
const QUALITY_DOWNGRADE: Record<ImageQuality, ImageQuality | null> = {
  high: 'medium',
  medium: 'low',
  low: null, // Can't downgrade further
};

/**
 * Detect network quality using Network Information API
 * Falls back to 'medium' if API unavailable
 */
function getNetworkQuality(): ImageQuality {
  const connection = (navigator as any).connection;

  if (!connection) {
    return 'medium'; // Default if API not available (iOS Safari, etc.)
  }

  const { effectiveType, downlink } = connection;

  // 4G with good bandwidth = high quality
  if (effectiveType === '4g' && downlink >= 5) {
    return 'high';
  }

  // 4G or 3G = medium quality
  if (effectiveType === '4g' || effectiveType === '3g') {
    return 'medium';
  }

  // 2G or slow-2g = low quality
  return 'low';
}

/**
 * Check if error is a timeout error
 */
function isTimeoutError(error: any): boolean {
  return (
    error?.code === 'ECONNABORTED' ||
    error?.code === 'ETIMEDOUT' ||
    error?.message?.includes('timeout')
  );
}

/**
 * Generate share image with adaptive quality retry
 *
 * Strategy:
 * 1. Detect network quality and request appropriate resolution
 * 2. On timeout, downgrade quality and retry (smaller file = faster delivery)
 * 3. Give up only when lowest quality also fails
 *
 * This ensures users on slow networks get *something* rather than nothing.
 */
export async function generateShareImage(data: ShareImageData): Promise<Blob> {
  let currentQuality = getNetworkQuality();
  let lastError: Error | null = null;

  while (currentQuality !== null) {
    try {
      const timeout = TIMEOUT_BY_QUALITY[currentQuality];

      const response = await apiClient.post(
        '/share/image',
        { ...data, quality: currentQuality },
        {
          responseType: 'blob',
          timeout,
        }
      );

      return response.data;
    } catch (error: any) {
      lastError = error;

      // Don't retry on 4xx errors (client errors)
      if (error?.response?.status >= 400 && error?.response?.status < 500) {
        throw error;
      }

      // On timeout or network error, try lower quality
      if (isTimeoutError(error) || error?.message === 'Network Error') {
        const nextQuality = QUALITY_DOWNGRADE[currentQuality];

        if (nextQuality) {
          console.log(
            `[ShareAPI] ${currentQuality} quality timed out, downgrading to ${nextQuality}`
          );
          currentQuality = nextQuality;
          // Small delay before retry
          await new Promise((resolve) => setTimeout(resolve, 500));
          continue;
        }
      }

      // No more retries possible
      break;
    }
  }

  // Log final failure to Sentry
  Sentry.captureException(lastError, {
    tags: { feature: 'share', action: 'generateShareImage' },
    extra: {
      shareData: data,
      finalQuality: currentQuality,
      networkQuality: getNetworkQuality(),
    },
  });

  throw lastError;
}

/**
 * Generate share image and return as base64 data URL
 * Uses network-aware quality selection
 */
export async function generateShareImageBase64(data: ShareImageData): Promise<string> {
  try {
    const quality = getNetworkQuality();
    const response = await apiClient.post<{ imageUrl: string }>(
      '/share/image-base64',
      { ...data, quality },
      { timeout: TIMEOUT_BY_QUALITY[quality] }
    );
    return response.data.imageUrl;
  } catch (error) {
    Sentry.captureException(error, {
      tags: { feature: 'share', action: 'generateShareImageBase64' },
      extra: { shareData: data },
    });
    throw error;
  }
}

/**
 * Download image as a file (fallback when Web Share API fails)
 */
function downloadImage(blob: Blob, filename: string): boolean {
  try {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    return true;
  } catch (error) {
    Sentry.captureException(error, {
      tags: { feature: 'share', action: 'downloadFallback' },
      extra: { blobSize: blob.size, filename },
    });
    return false;
  }
}

/**
 * Share image using Web Share API or fallback to download
 *
 * Note: Web Share API requires being called within a user gesture window (~1-5s).
 * If image generation takes too long, the gesture expires and share fails.
 * In that case, we automatically fall back to download.
 */
export async function shareImage(blob: Blob, filename = 'dasher-results.png'): Promise<boolean> {
  const file = new File([blob], filename, { type: 'image/png' });

  // Check if Web Share API supports files
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({
        files: [file],
        title: 'My Dasher Results',
        text: 'Check out my challenge results on Dasher!',
      });
      return true;
    } catch (error) {
      const errorName = (error as Error).name;

      // User cancelled - not an error, don't download
      if (errorName === 'AbortError') {
        return false;
      }

      // NotAllowedError = user gesture expired (image generation took too long)
      // Fall back to download silently (expected behavior, not an error)
      if (errorName === 'NotAllowedError') {
        return downloadImage(blob, filename);
      }

      // Other share failures - log to Sentry and fall back to download
      Sentry.captureException(error, {
        tags: { feature: 'share', action: 'webShareAPI' },
        extra: {
          blobSize: blob.size,
          filename,
          errorName,
          canShare: navigator.canShare?.({ files: [file] }),
        },
      });
      return downloadImage(blob, filename);
    }
  }

  // No Web Share API support - download the image
  return downloadImage(blob, filename);
}

/**
 * Generate progress share image with adaptive quality retry
 */
export async function generateProgressShareImage(data: ProgressShareData): Promise<Blob> {
  let currentQuality = getNetworkQuality();
  let lastError: Error | null = null;

  while (currentQuality !== null) {
    try {
      const timeout = TIMEOUT_BY_QUALITY[currentQuality];

      const response = await apiClient.post(
        '/share/progress',
        { ...data, quality: currentQuality },
        {
          responseType: 'blob',
          timeout,
        }
      );

      return response.data;
    } catch (error: any) {
      lastError = error;

      // Don't retry on 4xx errors (client errors)
      if (error?.response?.status >= 400 && error?.response?.status < 500) {
        throw error;
      }

      // On timeout or network error, try lower quality
      if (isTimeoutError(error) || error?.message === 'Network Error') {
        const nextQuality = QUALITY_DOWNGRADE[currentQuality];

        if (nextQuality) {
          console.log(
            `[ShareAPI] ${currentQuality} quality timed out, downgrading to ${nextQuality}`
          );
          currentQuality = nextQuality;
          await new Promise((resolve) => setTimeout(resolve, 500));
          continue;
        }
      }

      break;
    }
  }

  Sentry.captureException(lastError, {
    tags: { feature: 'share', action: 'generateProgressShareImage' },
    extra: {
      shareData: data,
      finalQuality: currentQuality,
      networkQuality: getNetworkQuality(),
    },
  });

  throw lastError;
}

/**
 * Generate challenge creation share image with adaptive quality retry
 */
export async function generateChallengeCreationShareImage(data: ChallengeCreationShareData): Promise<Blob> {
  let currentQuality = getNetworkQuality();
  let lastError: Error | null = null;

  while (currentQuality !== null) {
    try {
      const timeout = TIMEOUT_BY_QUALITY[currentQuality];

      const response = await apiClient.post(
        '/share/challenge-creation',
        { ...data, quality: currentQuality },
        {
          responseType: 'blob',
          timeout,
        }
      );

      return response.data;
    } catch (error: any) {
      lastError = error;

      // Don't retry on 4xx errors (client errors)
      if (error?.response?.status >= 400 && error?.response?.status < 500) {
        throw error;
      }

      // On timeout or network error, try lower quality
      if (isTimeoutError(error) || error?.message === 'Network Error') {
        const nextQuality = QUALITY_DOWNGRADE[currentQuality];

        if (nextQuality) {
          console.log(
            `[ShareAPI] ${currentQuality} quality timed out, downgrading to ${nextQuality}`
          );
          currentQuality = nextQuality;
          await new Promise((resolve) => setTimeout(resolve, 500));
          continue;
        }
      }

      break;
    }
  }

  Sentry.captureException(lastError, {
    tags: { feature: 'share', action: 'generateChallengeCreationShareImage' },
    extra: {
      shareData: data,
      finalQuality: currentQuality,
      networkQuality: getNetworkQuality(),
    },
  });

  throw lastError;
}

export const shareApi = {
  generateShareImage,
  generateShareImageBase64,
  generateProgressShareImage,
  generateChallengeCreationShareImage,
  shareImage,
};
